import { AIDiagnosis, Language, InvoiceItem } from '../types';

// The URL of your deployed Cloudflare Worker. 
// In development, this is typically http://localhost:8787.
// In production, update this to your worker's domain (e.g., https://cheang-ai-worker.your-name.workers.dev)
const WORKER_URL = (import.meta as any).env.VITE_WORKER_URL || 'http://localhost:8787';

/**
 * Generic helper to call the 'cheang-ai' Cloudflare Worker.
 */
async function callCheangAI(action: string, payload: any) {
  try {
    const response = await fetch(WORKER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, payload }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Worker returned ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error(`Failed to execute ${action}:`, err);
    return null;
  }
}

export const translateText = async (text: string, targetLang: Language): Promise<string> => {
  const result = await callCheangAI('translate', { text, targetLang });
  return result || text;
};

export const improveDescription = async (currentDescription: string, shopName: string, categories: string[], targetLang: Language): Promise<string | null> => {
  return await callCheangAI('improve_description', { currentDescription, shopName, categories, targetLang });
};

export const diagnoseIssue = async (userDescription: string, targetLang: Language, imageBase64?: string): Promise<AIDiagnosis | null> => {
  return await callCheangAI('diagnose', { userDescription, targetLang, imageBase64 });
};

export const analyzeIssueForSearch = async (imageBase64: string, targetLang: Language, guideText?: string): Promise<{ category: string, keyword: string, diagnosisSummary: string } | null> => {
  return await callCheangAI('analyze_search', { imageBase64, targetLang, guideText });
};

export const suggestReminders = async (input: string, targetLang: Language): Promise<any[]> => {
  const result = await callCheangAI('suggest_reminders', { input, targetLang });
  return result || [];
};

export const generateServiceImage = async (serviceName: string, description: string): Promise<string | null> => {
  return await callCheangAI('gen_service_image', { serviceName, description });
};

export const generateLogoImage = async (shopName: string, categories: string[]): Promise<string | null> => {
  return await callCheangAI('gen_logo', { shopName, categories });
};

export const generateShopImage = async (shopName: string, description: string): Promise<string | null> => {
  return await callCheangAI('gen_shop_image', { shopName, description });
};

export const generateInvoice = async (tasksText: string): Promise<InvoiceItem[]> => {
  const result = await callCheangAI('gen_invoice', { tasksText });
  return result || [];
};